import { useState, useEffect } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { authService } from '../services/auth'
import MarketingHeader from '../components/MarketingHeader'
import RecaptchaV3 from '../components/BotProtection/RecaptchaV3'
import RecaptchaV2 from '../components/BotProtection/RecaptchaV2'
import './DemoSignupPage.scss'

const VALID_PLANS = ['starter', 'professional', 'business', 'volume']

const DemoSignupPage = ({ getStarted = false }) => {
  const { t } = useTranslation(['auth', 'common'])

  // Form state
  const [companyName, setCompanyName] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [termsAccepted, setTermsAccepted] = useState(false)

  // Captcha state
  const [captchaToken, setCaptchaToken] = useState(null)
  const [captchaType, setCaptchaType] = useState('recaptcha-v3')
  const [showV2Fallback, setShowV2Fallback] = useState(false)

  // UI state
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [signupSuccess, setSignupSuccess] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [resendSuccess, setResendSuccess] = useState('')

  // Signup availability state
  const [signupsEnabled, setSignupsEnabled] = useState(null) // null = loading
  const [statusLoading, setStatusLoading] = useState(true)

  const [searchParams] = useSearchParams()

  // If arriving via /get-started?plan=X, store the plan in localStorage
  // so we can redirect to billing after login
  useEffect(() => {
    if (getStarted) {
      const plan = searchParams.get('plan')
      if (plan && VALID_PLANS.includes(plan.toLowerCase())) {
        localStorage.setItem('pendingPlan', plan.toLowerCase())
      } else {
        localStorage.setItem('pendingPlan', 'true')
      }
    }
  }, [getStarted, searchParams])

  // Check if signups are enabled on mount (uses different endpoint for /get-started vs /sign-up)
  useEffect(() => {
    const checkStatus = async () => {
      const status = getStarted
        ? await authService.getSelfServeSignupStatus()
        : await authService.getDemoSignupStatus()
      setSignupsEnabled(status.enabled)
      setStatusLoading(false)
    }
    checkStatus()
  }, [getStarted])

  // Password validation helpers
  const passwordChecks = {
    minLength: password.length >= 8,
    hasUppercase: /[A-Z]/.test(password),
    hasLowercase: /[a-z]/.test(password),
    hasNumber: /[0-9]/.test(password),
  }
  const passwordIsValid = Object.values(passwordChecks).every(Boolean)

  const handleV3Verify = ({ token }) => {
    setCaptchaToken(token)
    setCaptchaType('recaptcha-v3')
  }

  const handleV3Error = () => {
    setShowV2Fallback(true)
  }

  const handleV2Verify = ({ token }) => {
    setCaptchaToken(token)
    setCaptchaType('recaptcha-v2')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')

    // Validate fields
    if (!companyName.trim()) {
      setError(t('auth:demoSignup.companyNameRequired'))
      return
    }
    if (!firstName.trim()) {
      setError(t('auth:demoSignup.firstNameRequired'))
      return
    }
    if (!lastName.trim()) {
      setError(t('auth:demoSignup.lastNameRequired'))
      return
    }
    if (!email.trim()) {
      setError(t('auth:errors.emailRequired'))
      return
    }
    if (!passwordIsValid) {
      setError(t('auth:errors.weakPassword'))
      return
    }
    if (password !== confirmPassword) {
      setError(t('auth:errors.passwordMismatch'))
      return
    }
    if (!termsAccepted) {
      setError(t('auth:demoSignup.termsRequired'))
      return
    }
    if (!captchaToken) {
      setError(t('auth:demoSignup.captchaRequired'))
      return
    }

    setLoading(true)

    try {
      await authService.demoSignup({
        email,
        password,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        companyName: companyName.trim(),
        captchaToken,
        captchaType,
      })
      setSignupSuccess(true)
    } catch (err) {
      const status = err.response?.status
      const errorCode = err.response?.data?.error

      if (status === 409 || errorCode === 'EMAIL_EXISTS') {
        setError(t('auth:demoSignup.emailExists'))
      } else if (status === 403 || errorCode === 'SIGNUPS_DISABLED') {
        setError(t('auth:demoSignup.signupsDisabled'))
      } else {
        setError(err.response?.data?.message || t('auth:demoSignup.signupFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendVerification = async () => {
    setResendLoading(true)
    setResendSuccess('')

    try {
      await authService.resendVerification(email)
      setResendSuccess(t('auth:demoSignup.verificationResent'))
    } catch (err) {
      setError(err.response?.data?.message || t('auth:demoSignup.resendFailed'))
    } finally {
      setResendLoading(false)
    }
  }

  // Loading state while checking signup status
  if (statusLoading) {
    return (
      <div className="demo-signup-page">
        <MarketingHeader />
        <div className="demo-signup-page__card">
          <p className="demo-signup-page__loading">{t('common:buttons.loading')}</p>
        </div>
      </div>
    )
  }

  // Derive contact page URL from current hostname (strip app. subdomain)
  const getContactUrl = () => {
    if (import.meta.env.VITE_MARKETING_URL) return `${import.meta.env.VITE_MARKETING_URL}/contact.html`
    const host = window.location.hostname
    const marketingHost = host.startsWith('app.') ? host.slice(4) : host
    return `${window.location.protocol}//${marketingHost}/contact.html`
  }

  // Signups disabled
  if (!signupsEnabled) {
    return (
      <div className="demo-signup-page">
        <MarketingHeader />
        <div className="demo-signup-page__card">
          <h1 className="demo-signup-page__title">{getStarted ? t('auth:demoSignup.getStartedTitle') : t('auth:demoSignup.title')}</h1>
          <div className="demo-signup-page__disabled-message">
            <p>{t('auth:demoSignup.signupsUnavailable')}</p>
            <a href={getContactUrl()} className="demo-signup-page__link">
              {t('auth:demoSignup.requestAccount')}
            </a>
            <Link to="/login" className="demo-signup-page__link">
              {t('auth:demoSignup.goToLogin')}
            </Link>
          </div>
        </div>
      </div>
    )
  }

  // Success state
  if (signupSuccess) {
    return (
      <div className="demo-signup-page">
        <MarketingHeader />
        <div className="demo-signup-page__card">
          <h1 className="demo-signup-page__title">{t('auth:demoSignup.successTitle')}</h1>
          <div className="demo-signup-page__success-message">
            <p>{t('auth:demoSignup.checkEmail')}</p>
          </div>

          {resendSuccess && (
            <div className="demo-signup-page__success">
              {resendSuccess}
            </div>
          )}

          {error && (
            <div className="demo-signup-page__error">
              {error}
            </div>
          )}

          <button
            type="button"
            className="demo-signup-page__button demo-signup-page__button--secondary"
            onClick={handleResendVerification}
            disabled={resendLoading}
          >
            {resendLoading ? t('auth:demoSignup.resending') : t('auth:demoSignup.resendVerification')}
          </button>

          <div className="demo-signup-page__links">
            <Link to="/login">{t('auth:demoSignup.alreadyHaveAccount')}</Link>
          </div>
        </div>
      </div>
    )
  }

  // Signup form
  return (
    <div className="demo-signup-page">
      <MarketingHeader />
      <div className="demo-signup-page__card">
        <h1 className="demo-signup-page__title">{getStarted ? t('auth:demoSignup.getStartedTitle') : t('auth:demoSignup.title')}</h1>

        {error && (
          <div className="demo-signup-page__error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="demo-signup-page__field">
            <label>{t('auth:demoSignup.companyNameLabel')}</label>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              required
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="demo-signup-page__field-row">
            <div className="demo-signup-page__field">
              <label>{t('auth:demoSignup.firstNameLabel')}</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                required
                disabled={loading}
              />
            </div>

            <div className="demo-signup-page__field">
              <label>{t('auth:demoSignup.lastNameLabel')}</label>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="demo-signup-page__field">
            <label>{t('auth:demoSignup.emailLabel')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="demo-signup-page__field">
            <label>{t('auth:demoSignup.passwordLabel')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            {password.length > 0 && (
              <ul className="demo-signup-page__password-requirements">
                <li className={passwordChecks.minLength ? 'demo-signup-page__requirement--met' : 'demo-signup-page__requirement--unmet'}>
                  {t('auth:demoSignup.requirementLength')}
                </li>
                <li className={passwordChecks.hasUppercase ? 'demo-signup-page__requirement--met' : 'demo-signup-page__requirement--unmet'}>
                  {t('auth:demoSignup.requirementUppercase')}
                </li>
                <li className={passwordChecks.hasLowercase ? 'demo-signup-page__requirement--met' : 'demo-signup-page__requirement--unmet'}>
                  {t('auth:demoSignup.requirementLowercase')}
                </li>
                <li className={passwordChecks.hasNumber ? 'demo-signup-page__requirement--met' : 'demo-signup-page__requirement--unmet'}>
                  {t('auth:demoSignup.requirementNumber')}
                </li>
              </ul>
            )}
          </div>

          <div className="demo-signup-page__field">
            <label>{t('auth:demoSignup.confirmPasswordLabel')}</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={loading}
            />
          </div>

          <div className="demo-signup-page__captcha">
            {!showV2Fallback ? (
              <RecaptchaV3 onVerify={handleV3Verify} onError={handleV3Error} />
            ) : (
              <RecaptchaV2 onVerify={handleV2Verify} onError={() => setError(t('auth:demoSignup.captchaError'))} />
            )}
          </div>

          <div className="demo-signup-page__terms">
            <label className="demo-signup-page__checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                disabled={loading}
              />
              <span>
                {t('auth:demoSignup.agreeToTerms')}{' '}
                <Link to="/help/legal/terms-of-service" target="_blank" rel="noopener noreferrer">{t('auth:demoSignup.termsOfService')}</Link>
              </span>
            </label>
          </div>

          <button
            type="submit"
            className="demo-signup-page__button demo-signup-page__button--primary"
            disabled={loading}
          >
            {loading
              ? (getStarted ? t('auth:demoSignup.creatingAccountGetStarted') : t('auth:demoSignup.creatingAccount'))
              : (getStarted ? t('auth:demoSignup.createAccountButton') : t('auth:demoSignup.submitButton'))}
          </button>

          <div className="demo-signup-page__links">
            <Link to="/login">{t('auth:demoSignup.alreadyHaveAccount')}</Link>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DemoSignupPage
